import { useCallback, useContext, useState } from "react"

import { Index, MetricSeries, Metrics, latestMetric, metricPoints } from "wealthmode-frontend/backend"
import { Box, HStack, Heading, Spinner, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { colors } from "wealthmode-frontend/gluestack/ui/gluestack-ui-provider/config"
import { formatMoney, rgbToHex } from "wealthmode-frontend/util"

import { Timeseries, type TimeseriesAxis, type TimeseriesSeries } from "@/components/controls"
import { BackendContext } from "@/context"
import { useLoadOnFocus } from "@/lib/useLoadOnFocus"

// Totals across clients dwarf per-client averages, so the totals have an axis of their own
// beside the averages'. Each axis and its series share a hue from the theme's palette: totals
// gold, averages blue, the score mint. Amount axes rise from zero, and each keeps its series
// to a band of their own, the totals in the second fifth from the top and the averages in the
// second fifth from the bottom, so the two groups read apart. Scores run from 0 to 100, and
// their axis shows the whole scale so a score reads against it.
const GOLD = {
  axis: rgbToHex(colors.gold400),
  shades: [rgbToHex(colors.gold600), rgbToHex(colors.gold400), rgbToHex(colors.gold200)],
}
const BLUE = {
  axis: rgbToHex(colors.blue400),
  shades: [rgbToHex(colors.blue700), rgbToHex(colors.blue400), rgbToHex(colors.blue200)],
}
const MINT = rgbToHex(colors.mint600)

// The graph covers at least ten days, so a history only a few days long sits at the right
// rather than stretching across.
const MIN_SPAN = 9 * 24 * 60 * 60 * 1000

const AXES: TimeseriesAxis[] = [
  { id: "average", side: "left", min: 0, band: [0.2, 0.4], format: formatAmount, color: BLUE.axis },
  { id: "total", side: "left", min: 0, band: [0.6, 0.8], format: formatAmount, color: GOLD.axis },
  { id: "score", side: "right", min: 0, max: 100, format: formatScore },
]

export default function Dashboard() {
  const backend = useContext(BackendContext)

  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<Metrics | null>(null)

  const load = useCallback(async () => {
    const data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    const resolved = data.link("metrics")?.resolve() as Metrics | undefined
    if (resolved) {
      await resolved.fetch({ reload: true })
    }

    setMetrics(resolved ?? null)
    setLoading(false)
  }, [backend])

  useLoadOnFocus(load)

  if (loading) {
    return <Spinner size={64} />
  }

  if (!metrics) {
    return null
  }

  const m = metrics.attributes

  // Listed by group, totals then averages then the score, so the legend reads the same way.
  const series: TimeseriesSeries[] = [
    { name: "AUM Total", points: metricPoints(m.aum_total), axis: "total", color: GOLD.shades[0] },
    {
      name: "Rec Value Total",
      points: metricPoints(m.recommendation_opportunity_total),
      axis: "total",
      color: GOLD.shades[1],
    },
    {
      name: "Report Value Total",
      points: metricPoints(m.report_opportunity_total),
      axis: "total",
      color: GOLD.shades[2],
    },
    { name: "AUM Avg", points: metricPoints(m.aum_average), axis: "average", color: BLUE.shades[0] },
    {
      name: "Rec Value Avg",
      points: metricPoints(m.recommendation_opportunity_average),
      axis: "average",
      color: BLUE.shades[1],
    },
    {
      name: "Report Value Avg",
      points: metricPoints(m.report_opportunity_average),
      axis: "average",
      color: BLUE.shades[2],
    },
    { name: "Score (value-weighted)", points: metricPoints(m.score_weighted_average), axis: "score", color: MINT },
  ]

  return (
    <Box className="flex-1 min-h-0 overflow-y-auto p-4">
      <VStack className="gap-8">
        <HStack className="gap-4 flex-wrap">
          <Headline
            title="AUM"
            total={m.aum_total}
            average={m.aum_average}
            median={m.aum_median}
          />
          <Headline
            title="Rec Value"
            total={m.recommendation_opportunity_total}
            average={m.recommendation_opportunity_average}
            median={m.recommendation_opportunity_median}
          />
          <Headline
            title="Report Value"
            total={m.report_opportunity_total}
            average={m.report_opportunity_average}
            median={m.report_opportunity_median}
          />
          <Panel
            title="Score"
            centered
          >
            <FigurePlaceholder />
            <HStack className="gap-8">
              <Figure
                label="Avg"
                value={formatScore(latestMetric(m.score_weighted_average))}
              />
              <Figure
                label="Median"
                value={formatScore(latestMetric(m.score_median))}
              />
            </HStack>
          </Panel>
        </HStack>

        <Panel title="Over time">
          <Timeseries
            series={series}
            axes={AXES}
            height={360}
            minSpan={MIN_SPAN}
          />
        </Panel>
      </VStack>
    </Box>
  )
}

/** A metric's latest total, with its average and median side by side below it. */
function Headline({
  title,
  total,
  average,
  median,
}: {
  title: string
  total: MetricSeries
  average: MetricSeries
  median: MetricSeries
}) {
  return (
    <Panel
      title={title}
      centered
    >
      <Figure
        label="Total"
        value={formatAmount(latestMetric(total))}
      />
      <HStack className="gap-8">
        <Figure
          label="Avg"
          value={formatAmount(latestMetric(average))}
        />
        <Figure
          label="Median"
          value={formatAmount(latestMetric(median))}
        />
      </HStack>
    </Panel>
  )
}

function Panel({ title, centered, children }: { title: string; centered?: boolean; children: React.ReactNode }) {
  return (
    <VStack
      className={`flex-1 min-w-64 gap-4 rounded-md border border-border-medium bg-background-secondary p-4 ${centered ? "items-center" : ""}`}
    >
      <Heading
        size="sm"
        className="whitespace-nowrap"
      >
        {title}
      </Heading>
      <VStack className={`gap-4 ${centered ? "items-center" : ""}`}>{children}</VStack>
    </VStack>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <VStack className="items-center">
      <Text className="text-sm text-foreground-tertiary whitespace-nowrap">{label}</Text>
      <Text className="text-2xl font-bold text-foreground-primary">{value}</Text>
    </VStack>
  )
}

/** Holds a figure's space unseen, so the figures below it line up with their neighbours'. */
function FigurePlaceholder() {
  return (
    <Box
      className="opacity-0"
      aria-hidden
    >
      <Figure
        label="-"
        value="-"
      />
    </Box>
  )
}

function formatAmount(amount: number | null): string {
  return formatMoney(amount) ?? ""
}

function formatScore(score: number | null): string {
  return score === null ? "--" : score.toFixed(1)
}
